import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Upload, X, Plus, Loader2 } from 'lucide-react';
import { TableScroll } from '../components/ui/table-scroll.js';
import {
  listCampaigns,
  getCampaign,
  createCampaign,
  closeCampaign,
  reopenCampaign,
  addCampaignMember,
  removeCampaignMember,
  uploadRoster,
  listRecipients,
  addRecipient,
  removeRecipient,
  updateNotifyConfig,
  getNotificationLog,
  sendTestNotification,
  type RosterUploadSummary,
} from '../lib/api/campaigns.js';

export function CampaignsPage() {
  const queryClient = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newEvent, setNewEvent] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [asxCode, setAsxCode] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [uploadSummary, setUploadSummary] = useState<RosterUploadSummary | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const campaignsQuery = useQuery({
    queryKey: ['campaigns', showClosed ? 'all' : 'active'],
    queryFn: () => listCampaigns(showClosed ? undefined : 'active'),
  });

  const detailQuery = useQuery({
    queryKey: ['campaign', selectedId],
    queryFn: () => getCampaign(selectedId as string),
    enabled: !!selectedId,
  });

  const recipientsQuery = useQuery({
    queryKey: ['campaign-recipients', selectedId],
    queryFn: () => listRecipients(selectedId as string),
    enabled: !!selectedId,
  });

  const logQuery = useQuery({
    queryKey: ['campaign-log', selectedId],
    queryFn: () => getNotificationLog(selectedId as string),
    enabled: !!selectedId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    if (selectedId) queryClient.invalidateQueries({ queryKey: ['campaign', selectedId] });
  };

  const invalidateRecipients = () =>
    queryClient.invalidateQueries({ queryKey: ['campaign-recipients', selectedId] });

  const createMut = useMutation({
    mutationFn: () =>
      createCampaign({
        name: newName.trim(),
        event_name: newEvent.trim() || null,
        event_date: newEventDate || null,
      }),
    onSuccess: (campaign) => {
      setNewName('');
      setNewEvent('');
      setNewEventDate('');
      setSelectedId(campaign.campaign_id);
      invalidate();
    },
  });

  const closeMut = useMutation({
    mutationFn: (id: string) => closeCampaign(id),
    onSuccess: invalidate,
  });

  const reopenMut = useMutation({
    mutationFn: (id: string) => reopenCampaign(id),
    onSuccess: invalidate,
  });

  const addMemberMut = useMutation({
    mutationFn: () =>
      addCampaignMember(selectedId as string, {
        asx_code: asxCode.trim().toUpperCase(),
        company_name: companyName.trim() || undefined,
      }),
    onSuccess: () => {
      setAsxCode('');
      setCompanyName('');
      invalidate();
    },
  });

  const removeMemberMut = useMutation({
    mutationFn: (entityId: string) =>
      removeCampaignMember(selectedId as string, entityId),
    onSuccess: invalidate,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadRoster(selectedId as string, file),
    onSuccess: (summary) => {
      setUploadSummary(summary);
      invalidate();
    },
  });

  const addRecipientMut = useMutation({
    mutationFn: () =>
      addRecipient(selectedId as string, {
        email: recipientEmail.trim(),
        name: recipientName.trim() || undefined,
      }),
    onSuccess: () => {
      setRecipientEmail('');
      setRecipientName('');
      invalidateRecipients();
    },
  });

  const removeRecipientMut = useMutation({
    mutationFn: (recipientId: string) =>
      removeRecipient(selectedId as string, recipientId),
    onSuccess: invalidateRecipients,
  });

  const notifyConfigMut = useMutation({
    mutationFn: (body: { notify_enabled?: boolean; notify_cadence?: 'immediate' | 'daily_digest' }) =>
      updateNotifyConfig(selectedId as string, body),
    onSuccess: invalidate,
  });

  const testMut = useMutation({
    mutationFn: () => sendTestNotification(selectedId as string),
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedId) {
      setUploadSummary(null);
      uploadMut.mutate(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const campaigns = campaignsQuery.data ?? [];
  const detail = detailQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="h-7 w-7 text-indigo-600" />
        <h1 className="text-2xl font-semibold">Campaigns</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List + create */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-surface rounded-lg border p-4 space-y-3">
            <h2 className="font-medium">New campaign</h2>
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Campaign name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Event name (optional)"
              value={newEvent}
              onChange={(e) => setNewEvent(e.target.value)}
            />
            <input
              type="date"
              className="w-full border rounded px-3 py-2 text-sm"
              value={newEventDate}
              onChange={(e) => setNewEventDate(e.target.value)}
            />
            <button
              className="w-full bg-indigo-600 text-white rounded px-3 py-2 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              disabled={!newName.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create
            </button>
            {createMut.isError && (
              <p className="text-sm text-red-600">
                Create failed:{' '}
                {(createMut.error as any)?.response?.data?.detail ??
                  (createMut.error as any)?.message ??
                  'error'}
              </p>
            )}
          </div>

          <div className="bg-surface rounded-lg border">
            <div className="flex items-center justify-between p-3 border-b">
              <h2 className="font-medium">Campaigns</h2>
              <label className="text-xs flex items-center gap-1 text-gray-600">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                />
                Show closed
              </label>
            </div>
            {campaignsQuery.isLoading ? (
              <div className="p-4 text-sm text-gray-500">Loading…</div>
            ) : campaigns.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No campaigns yet.</div>
            ) : (
              <ul className="divide-y">
                {campaigns.map((c) => (
                  <li key={c.campaign_id}>
                    <button
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                        selectedId === c.campaign_id ? 'bg-indigo-50' : ''
                      }`}
                      onClick={() => {
                        setSelectedId(c.campaign_id);
                        setUploadSummary(null);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{c.name}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            c.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {c.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {c.member_count ?? 0} companies
                        {c.event_name ? ` · ${c.event_name}` : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedId ? (
            <div className="bg-surface rounded-lg border p-8 text-center text-gray-500">
              Select a campaign to view its companies and upload a roster.
            </div>
          ) : detailQuery.isLoading || !detail ? (
            <div className="bg-surface rounded-lg border p-8 text-center text-gray-500">
              Loading…
            </div>
          ) : (
            <>
              <div className="bg-surface rounded-lg border p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">{detail.name}</h2>
                    <p className="text-sm text-gray-500">
                      {detail.event_name || 'No event'}
                      {detail.event_date ? ` · ${detail.event_date}` : ''} ·{' '}
                      {detail.status}
                    </p>
                  </div>
                  {detail.status === 'active' ? (
                    <button
                      className="text-sm border border-red-300 text-red-600 rounded px-3 py-1.5 disabled:opacity-50"
                      disabled={closeMut.isPending}
                      onClick={() => {
                        if (confirm('Close this campaign? Companies not in any other active campaign will stop being monitored as campaign entities.')) {
                          closeMut.mutate(detail.campaign_id);
                        }
                      }}
                    >
                      Close campaign
                    </button>
                  ) : (
                    <button
                      className="text-sm border border-green-400 text-green-700 rounded px-3 py-1.5 disabled:opacity-50"
                      disabled={reopenMut.isPending}
                      onClick={() => reopenMut.mutate(detail.campaign_id)}
                    >
                      {reopenMut.isPending ? 'Reopening…' : 'Reopen campaign'}
                    </button>
                  )}
                </div>
              </div>

              {/* Roster upload + add member */}
              {detail.status === 'active' && (
                <div className="bg-surface rounded-lg border p-4 space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">Upload roster spreadsheet</h3>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFile}
                      className="hidden"
                    />
                    <button
                      className="text-sm bg-gray-100 hover:bg-gray-200 rounded px-3 py-2 flex items-center gap-2 disabled:opacity-50"
                      disabled={uploadMut.isPending}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadMut.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      Choose .csv / .xlsx
                    </button>
                    {uploadMut.isError && (
                      <p className="text-sm text-red-600 mt-2">
                        Upload failed: {(uploadMut.error as any)?.response?.data?.detail ?? 'error'}
                      </p>
                    )}
                    {uploadSummary && (
                      <div className="text-sm text-gray-700 mt-2 bg-green-50 border border-green-200 rounded p-3">
                        Parsed {uploadSummary.rows_parsed} rows ·{' '}
                        {uploadSummary.companies_added} ASX companies
                        {uploadSummary.other_exchange_added > 0 &&
                          ` · ${uploadSummary.other_exchange_added} other-exchange`}
                        {uploadSummary.private_companies_added > 0 &&
                          ` · ${uploadSummary.private_companies_added} private`}{' '}
                        · {uploadSummary.contacts_ingested} contacts ingested
                        {uploadSummary.contacts_needs_review > 0 &&
                          ` · ${uploadSummary.contacts_needs_review} need review`}
                        {uploadSummary.contacts_failed > 0 &&
                          ` · ${uploadSummary.contacts_failed} contacts failed`}
                        {uploadSummary.rows_skipped > 0 &&
                          ` · ${uploadSummary.rows_skipped} rows skipped`}
                        {uploadSummary.skipped_reasons?.length > 0 && (
                          <ul className="mt-2 text-xs text-gray-600 list-disc pl-4">
                            {uploadSummary.skipped_reasons.map((s, i) => (
                              <li key={i}>
                                {s.company || '(no company name)'} — {s.reason}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-2">Add company by ASX code</h3>
                    <div className="flex gap-2">
                      <input
                        className="border rounded px-3 py-2 text-sm w-28 uppercase"
                        placeholder="ASX"
                        value={asxCode}
                        onChange={(e) => setAsxCode(e.target.value)}
                      />
                      <input
                        className="border rounded px-3 py-2 text-sm flex-1"
                        placeholder="Company name (optional)"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                      <button
                        className="bg-indigo-600 text-white rounded px-3 py-2 text-sm disabled:opacity-50"
                        disabled={!asxCode.trim() || addMemberMut.isPending}
                        onClick={() => addMemberMut.mutate()}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Members */}
              <div className="bg-surface rounded-lg border">
                <div className="p-3 border-b font-medium">
                  Companies ({detail.members.length})
                </div>
                {detail.members.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No companies yet.</div>
                ) : (
                  <TableScroll>
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-500 border-b">
                      <tr>
                        <th className="px-4 py-2 font-medium">Ticker</th>
                        <th className="px-4 py-2 font-medium">Company</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {detail.members.map((m) => (
                        <tr key={m.entity_id}>
                          <td className="px-4 py-2 font-mono">
                            {m.entity_type === 'company' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                private
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5">
                                {m.exchange && m.exchange !== 'ASX' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-sans">
                                    {m.exchange}
                                  </span>
                                )}
                                {m.canonical_identifier}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {m.display_name}
                            {m.entity_type === 'company' && (
                              <span className="text-gray-400 text-xs ml-2">
                                {m.canonical_identifier}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {detail.status === 'active' && (
                              <button
                                className="text-gray-400 hover:text-red-600"
                                title="Remove from campaign"
                                onClick={() => removeMemberMut.mutate(m.entity_id)}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </TableScroll>
                )}
              </div>

              {/* Notifications */}
              <div className="bg-surface rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Internal notifications</h3>
                  <div className="flex items-center gap-3">
                    <button
                      className="text-sm border rounded px-3 py-1.5 disabled:opacity-50"
                      disabled={testMut.isPending || (recipientsQuery.data ?? []).length === 0}
                      onClick={() => testMut.mutate()}
                      title="Send a test email to this campaign's recipients"
                    >
                      {testMut.isPending ? 'Sending…' : 'Send test'}
                    </button>
                    <label className="text-sm flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={detail.notify_enabled ?? true}
                        onChange={(e) =>
                          notifyConfigMut.mutate({ notify_enabled: e.target.checked })
                        }
                      />
                      Enabled
                    </label>
                  </div>
                </div>
                {testMut.data && (
                  <p className="text-sm text-green-700">
                    Test sent to {testMut.data.sent}/{testMut.data.recipients.length} recipient
                    {testMut.data.recipients.length === 1 ? '' : 's'}
                    {testMut.data.failed > 0 ? ` · ${testMut.data.failed} failed` : ''}
                    {testMut.data.error ? ` · ${testMut.data.error}` : ''}
                  </p>
                )}
                {testMut.isError && (
                  <p className="text-sm text-red-600">
                    Test failed: {(testMut.error as any)?.response?.data?.detail ?? 'error'}
                  </p>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Cadence</span>
                  <select
                    className="border rounded px-2 py-1"
                    value={detail.notify_cadence ?? 'immediate'}
                    onChange={(e) =>
                      notifyConfigMut.mutate({
                        notify_cadence: e.target.value as 'immediate' | 'daily_digest',
                      })
                    }
                  >
                    <option value="immediate">Immediate (per article)</option>
                    <option value="daily_digest">Daily digest (batched)</option>
                  </select>
                </div>

                <div className="border-t pt-3">
                  <div className="flex gap-2 mb-2">
                    <input
                      className="border rounded px-3 py-2 text-sm flex-1"
                      placeholder="rep@company.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                    <input
                      className="border rounded px-3 py-2 text-sm w-40"
                      placeholder="Name (optional)"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                    <button
                      className="bg-indigo-600 text-white rounded px-3 py-2 text-sm disabled:opacity-50"
                      disabled={!recipientEmail.trim() || addRecipientMut.isPending}
                      onClick={() => addRecipientMut.mutate()}
                    >
                      Add rep
                    </button>
                  </div>
                  {(recipientsQuery.data ?? []).length === 0 ? (
                    <p className="text-sm text-gray-500">No recipients yet.</p>
                  ) : (
                    <ul className="divide-y border rounded">
                      {(recipientsQuery.data ?? []).map((r) => (
                        <li
                          key={r.recipient_id}
                          className="flex items-center justify-between px-3 py-2 text-sm"
                        >
                          <span>
                            {r.email}
                            {r.name ? <span className="text-gray-500"> · {r.name}</span> : null}
                          </span>
                          <button
                            className="text-gray-400 hover:text-red-600"
                            onClick={() => removeRecipientMut.mutate(r.recipient_id)}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {(logQuery.data ?? []).length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-gray-500 mb-1">Recent sends</p>
                    <ul className="text-xs text-gray-600 space-y-0.5 max-h-40 overflow-y-auto">
                      {(logQuery.data ?? []).slice(0, 20).map((l) => (
                        <li key={l.log_id}>
                          <span
                            className={
                              l.status === 'failed' ? 'text-red-600' : 'text-green-700'
                            }
                          >
                            {l.status}
                          </span>{' '}
                          → {l.recipient_email} ·{' '}
                          {new Date(l.sent_at).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CampaignsPage;
